import React, { useEffect, useRef, useState } from 'react';
import { View, Text, ScrollView } from 'react-native';
import PropTypes from 'prop-types';
import { t } from 'Locale';
import { useRecordingCalls } from 'Utils/hooks/useRecordingCalls';
import { ActivityIndicator, ModalHeader, Hr, ConfirmationModal } from 'LocalGluons';
import {
    getPlatformsStorageString,
    getRecordingForItem,
    getRecordingIdsString,
    getRecordingIdsStringByStorage
} from 'Utils/itemUtils';
import { ITEM_TYPES } from 'Utils';
import { useStore } from 'Store';
import { RecordPlatformSelector } from 'Pages/RecordPage/RecordPlatformSelector/RecordPlatformSelector';
import { RecordPlatformEnum } from '@24i/st-api-connector';
import { RecordPageButtons } from './RecordPageButtons';

import defaultStyles from './RecordPage.jss';

const getRecPlatformsFromUserData = (userData) => ({
    iptv:
        userData && userData.platforms && userData.platforms.length > 0
            ? userData.platforms.includes('IPTV')
            : false,
    go:
        userData && userData.platforms && userData.platforms.length > 0
            ? userData.platforms.includes('GO')
            : false
});

export const recordingPages = Object.freeze({
    setRecordings: 'setRecordings',
    cancelRecordings: 'cancelRecordings',
    deleteRecordings: 'deleteRecordings',
    setRecordingsSimple: 'setRecordingsSimple',
    cancelRecordingsSimple: 'cancelRecordingsSimple',
    deleteRecordingSimple: 'deleteRecordingSimple'
});

export const pageConfig = {
    [recordingPages.setRecordings]: (allowRecPlatformSelect) => ({
        title: 'RECORDINGS.RECORDING_OPTIONS',
        buttonTitle: 'COMMON.RECORD',
        description: allowRecPlatformSelect
            ? 'RECORDINGS.SELECT_PLATFORM'
            : 'RECORDINGS.RECORD_TO_INFO',
        footerDescription: 'RECORDINGS.SERIES_NOT_REC_DUPLICATES',
        buttons: [
            {
                id: 'record-episode',
                label: t('RECORDINGS.RECORD_EPISODE'),
                isSelected: true,
                testID: 'episode_record'
            },
            {
                id: 'record-series',
                label: t('RECORDINGS.RECORD_SERIES'),
                testID: 'series_record'
            }
        ]
    }),
    [recordingPages.cancelRecordings]: () => ({
        title: 'RECORDINGS.RECORD_CANCEL',
        buttonTitle: 'RECORDINGS.BTN_CANCEL_RECORDING',
        buttons: [
            {
                id: 'cancel-episode',
                label: t('RECORDINGS.RECORD_CANCEL_EPISODE'),
                isSelected: true,
                testID: 'cancel_episode_record'
            },
            {
                id: 'cancel-all-episodes',
                label: t('RECORDINGS.RECORD_CANCEL_ALL_EPISODES'),
                testID: 'cancel_all_episodes_record'
            }
        ]
    }),
    [recordingPages.deleteRecordings]: () => ({
        title: 'RECORDINGS.DELETE_RECORDING',
        buttonTitle: 'RECORDINGS.BTN_DELETE',
        buttons: [
            {
                id: 'delete-episode',
                label: t('RECORDINGS.RECORD_DELETE_EPISODE'),
                isSelected: true,
                testID: 'delete_episode_record'
            },
            {
                id: 'delete-all-episodes',
                label: t('RECORDINGS.RECORD_DELETE_ALL_EPISODES'),
                testID: 'delete_all_episodes_records'
            }
        ]
    }),
    [recordingPages.setRecordingsSimple]: (allowRecPlatformSelect) => ({
        title: 'RECORDINGS.RECORDING_OPTIONS',
        buttonTitle: 'COMMON.RECORD',
        description: allowRecPlatformSelect
            ? 'RECORDINGS.SELECT_PLATFORM'
            : 'RECORDINGS.RECORD_TO_INFO'
    }),
    [recordingPages.cancelRecordingsSimple]: () => ({
        title: 'RECORDINGS.RECORD_CANCEL',
        buttonTitle: 'RECORDINGS.BTN_CANCEL_RECORDING',
        description: 'RECORDINGS.BTN_CANCEL_RECORDING',
        footerDescription: 'RECORDINGS.CANCEL_RECORDING_DESCRIPTION'
    }),
    [recordingPages.deleteRecordingSimple]: () => ({
        title: 'RECORDINGS.DELETE_RECORDING',
        buttonTitle: 'RECORDINGS.BTN_DELETE',
        description: 'RECORDINGS.DELETE_RECORDING_QUESTION'
    })
};

// eslint-disable-next-line max-statements
export const RecordPage = ({
    item,
    page,
    onClose,
    onRecordStateChanged,
    orientation,
    style,
    withModalHeader,
    width,
    setError,
    setModalContent
}) => {
    const { userData } = useStore();
    const { record, cancelRecording, deleteRecording, isRecordingStorageFull } =
        useRecordingCalls(item);
    const [loading, setLoading] = useState(false);
    // Which platforms can user record
    const userRecPlatforms = useRef(getRecPlatformsFromUserData(userData));
    const { ids = {} } = getRecordingForItem(item);
    // const [showFullRecordStorageDialog, setShowFullRecordStorageDialog] = useState(false);
    const seriesIsSelected = useRef(false);
    const storage = getPlatformsStorageString(ids);
    const recordedOnBothPlatforms = Boolean(ids.iptv && ids.go);
    const isLandscape = orientation === 'landscape';
    const onRecord = async (isSeries, skipStorageCheck = false) => {
        setLoading(true);
        const recPlatforms = { iptv: false, go: false };
        recPlatforms.iptv =
            item.isRecordableOnPlatform(RecordPlatformEnum.Iptv) && userRecPlatforms.current.iptv;
        recPlatforms.go =
            item.isRecordableOnPlatform(RecordPlatformEnum.Go) && userRecPlatforms.current.go;
        const platformsString = getPlatformsStorageString(recPlatforms);
        try {
            seriesIsSelected.current = isSeries;
            if (!skipStorageCheck) {
                const isStorageFull = await isRecordingStorageFull(platformsString);
                if (isStorageFull) {
                    setModalContent(
                        <ConfirmationModal
                            header={t('RECORDINGS.FULL_CAPACITY_WARNING_TITLE')}
                            confirmLabel={t('COMMON.RECORD')}
                            cancelLabel={t('COMMON.CANCEL')}
                            message={t('RECORDINGS.FULL_CAPACITY_WARNING_DESCRIPTION')}
                            onConfirm={onFullRecordStorageDialogConfirm}
                            onCancel={closeFullRecordStorageDialog}
                            mode={isLandscape ? 'landscape' : 'portrait'}
                        />
                    );
                    // setShowFullRecordStorageDialog(true);
                    setLoading(false);
                    return;
                }
            }
            await record(isSeries, platformsString);
            setLoading(false);
            onRecordStateChanged();
            onClose();
        } catch (error) {
            setLoading(false);
            setError({ error, screenId: 'recordPage' });
        }
    };

    const onCancelRecording = async (isSeries) => {
        setLoading(true);
        try {
            const selectedStorage = recordedOnBothPlatforms
                ? getPlatformsStorageString(userRecPlatforms.current)
                : storage;
            const selectedIdList = recordedOnBothPlatforms
                ? getRecordingIdsStringByStorage(ids, userRecPlatforms.current)
                : getRecordingIdsString(ids);
            await cancelRecording(isSeries, selectedStorage, selectedIdList);
            setLoading(false);
            onRecordStateChanged();
            onClose(isSeries);
        } catch (error) {
            setLoading(false);
            setError({ error, screenId: 'recordPage' });
        }
    };

    const onDeleteRecording = async (isSeries) => {
        setLoading(true);
        try {
            const selectedStorage = recordedOnBothPlatforms
                ? getPlatformsStorageString(userRecPlatforms.current)
                : storage;
            const selectedIdList = recordedOnBothPlatforms
                ? getRecordingIdsStringByStorage(ids, userRecPlatforms.current)
                : getRecordingIdsString(ids);
            await deleteRecording(isSeries, selectedStorage, selectedIdList);
            setLoading(false);
            onRecordStateChanged();
            onClose(item.modelType !== ITEM_TYPES.TV);
        } catch (error) {
            setLoading(false);
            setError({ error, screenId: 'recordPage' });
        }
    };

    const pageComponents = {
        [recordingPages.setRecordings]: {
            key: recordingPages.setRecordings,
            isLandscape,
            page: recordingPages.setRecordings,
            onCancel: onClose,
            onAccept: onRecord
        },
        [recordingPages.cancelRecordings]: {
            key: recordingPages.cancelRecordings,
            isLandscape,
            displayExitCancel: !isLandscape,
            page: recordingPages.cancelRecordings,
            onCancel: onClose,
            onAccept: onCancelRecording
        },
        [recordingPages.deleteRecordings]: {
            key: recordingPages.deleteRecordings,
            isLandscape,
            page: recordingPages.deleteRecordings,
            onCancel: onClose,
            onAccept: onDeleteRecording
        },
        [recordingPages.setRecordingsSimple]: {
            key: recordingPages.setRecordingsSimple,
            isLandscape,
            page: recordingPages.setRecordingsSimple,
            onCancel: onClose,
            onAccept: onRecord
        },
        [recordingPages.cancelRecordingsSimple]: {
            key: recordingPages.cancelRecordingsSimple,
            isLandscape,
            displayExitCancel: !isLandscape,
            page: recordingPages.cancelRecordingsSimple,
            onCancel: onClose,
            onAccept: onCancelRecording
        },
        [recordingPages.deleteRecordingSimple]: {
            key: recordingPages.deleteRecordingSimple,
            isLandscape,
            displayExitCancel: false,
            page: recordingPages.deleteRecordingSimple,
            onCancel: onClose,
            onAccept: onDeleteRecording
        }
    };

    const closeFullRecordStorageDialog = () => {
        setModalContent(null);
        // setShowFullRecordStorageDialog(false);
    };

    const onFullRecordStorageDialogConfirm = () => {
        setModalContent(null);
        // setShowFullRecordStorageDialog(false);
        onRecord(seriesIsSelected.current, true);
    };

    useEffect(() => {
        userRecPlatforms.current = getRecPlatformsFromUserData(userData);
    }, [userData]);

    const isSetting =
        page === recordingPages.setRecordings || page === recordingPages.setRecordingsSimple;

    const isRecordableOnGo = item.isRecordableOnPlatform(RecordPlatformEnum.Go);
    const userCanRecordOnIptv = userData && userData.platforms.includes('IPTV');
    const isRecordableOnIptv =
        item.isRecordableOnPlatform(RecordPlatformEnum.Iptv) && userCanRecordOnIptv;

    let allowRecPlatformSelect = false;
    if (isSetting) {
        allowRecPlatformSelect = isRecordableOnGo && isRecordableOnIptv;
    } else {
        allowRecPlatformSelect = ids.iptv && ids.go;
    }

    const recordPageButtonsProps = pageComponents[page];
    const { description, footerDescription } = pageConfig[page](allowRecPlatformSelect, ids);
    let translatedDescription = t(description);
    if (!allowRecPlatformSelect) {
        let availablePlatform;
        if (isSetting) {
            availablePlatform = isRecordableOnIptv
                ? t('COMMON.REC_PLATFORM_IPTV')
                : t('COMMON.REC_PLATFORM_OTT');
        } else {
            availablePlatform = ids.iptv
                ? t('COMMON.REC_PLATFORM_IPTV')
                : t('COMMON.REC_PLATFORM_OTT');
        }
        if (description) {
            translatedDescription = `${translatedDescription} ${availablePlatform}`;
        }
    }

    return (
        <>
            <View style={[defaultStyles.container, style]}>
                {/* <Modal
                    animationType="slide"
                    transparent
                    visible={showFullRecordStorageDialog}
                    onRequestClose={closeFullRecordStorageDialog}
                    supportedOrientations={['portrait', 'landscape']}
                >
                    <FullRecordStorageDialog
                        mode={isLandscape ? 'landscape' : 'portrait'}
                        onCancel={closeFullRecordStorageDialog}
                        onConfirm={onFullRecordStorageDialogConfirm}
                    />
                </Modal> */}
                {withModalHeader && !isLandscape && <ModalHeader onPress={onClose} />}
                <ScrollView
                    style={[defaultStyles.scrollView, width && { width }]}
                    contentContainerStyle={[defaultStyles.scrollContainer]}
                >
                    <>
                        <Text style={defaultStyles.title}>{t(pageConfig[page]().title)}</Text>
                        {withModalHeader && !isLandscape && <Hr style={defaultStyles.separator} />}
                        {Boolean(translatedDescription) && (
                            <Text style={defaultStyles.description}>{translatedDescription}</Text>
                        )}
                        <RecordPageButtons {...recordPageButtonsProps}>
                            {allowRecPlatformSelect && (
                                <>
                                    <RecordPlatformSelector
                                        isLandscape={isLandscape}
                                        onGoPress={(checked) => {
                                            userRecPlatforms.current.go = checked;
                                        }}
                                        onIptvPress={(checked) => {
                                            userRecPlatforms.current.iptv = checked;
                                        }}
                                    />
                                    {isLandscape && <Hr style={defaultStyles.separator} />}
                                </>
                            )}
                        </RecordPageButtons>
                        {Boolean(footerDescription) && (
                            <Text style={defaultStyles.description}>{t(footerDescription)}</Text>
                        )}
                    </>
                </ScrollView>
            </View>
            {loading && <ActivityIndicator isOverlay />}
        </>
    );
};

RecordPage.defaultProps = {
    orientation: 'portrait',
    style: {},
    withModalHeader: true,
    width: undefined,
    setError: (error) => {
        console.log(error);
    }
};

RecordPage.propTypes = {
    style: PropTypes.shape({}),
    orientation: PropTypes.string,
    item: PropTypes.shape({}).isRequired,
    page: PropTypes.string.isRequired,
    onClose: PropTypes.func.isRequired,
    onRecordStateChanged: PropTypes.func.isRequired,
    withModalHeader: PropTypes.bool,
    width: PropTypes.number,
    setError: PropTypes.func
};
